import { Card, Button, Flex, Drawer, Input } from "antd";
import React, { useEffect, useMemo, useState } from "react";
import { useSelector, useDispatch } from "react-redux";

import { css } from "@emotion/css";
import MoreEmptyState from "../../../../../../Theme/EmptyStates/MoreEmptyStates/MoreEmptyState";
import CustomLoaderIcon from "../../../../../../Theme/CustomIcons/Icons/Essetional/CustomLoaderIcon";
import MessageTextIcon from "../../../../../../Theme/CustomIcons/Icons/Emails,Messages/MessageTextIcon";
import {
  addEntityCommentsAction,
  getEntityCommentsAction,
  getEntityActivityLogsAction,
} from "../../../../../../store/action/admin/entityActions";
import MaximizeIcon from "../../../../../../Theme/CustomIcons/Icons/Grid/MaximizeIcon";

const CommentsCard = ({
  entityId,
  open = false,
  onClose,
  showCard = true,
  comments,
  commentsLoading: externalLoading,
}) => {
  const themeDefault = useSelector((state) => state.themeDefault);
  const isLightBg = themeDefault?.info?.themeColor === "default";
  const dispatch = useDispatch();

  const [drawerOpen, setDrawerOpen] = useState(open);
  const [commentText, setCommentText] = useState("");
  const [loading, setLoading] = useState(false);

  // Get comments from Redux store
  const entityCommentsState = useSelector((state) => state.getEntityComments);
  // Use Redux state if available, otherwise fallback to props
  const commentsData = Array.isArray(entityCommentsState?.info)
    ? entityCommentsState.info
    : Array.isArray(comments)
    ? comments
    : [];

  const commentsLoading =
    entityCommentsState?.loading || externalLoading || false;

  useEffect(() => {
    setDrawerOpen(open);
  }, [open]);

  // Load comments when entityId changes or drawer opens
  useEffect(() => {
    if (entityId && (open || drawerOpen)) {
      dispatch(getEntityCommentsAction(entityId));
    }
  }, [entityId, open, drawerOpen, dispatch]);

  const getUserInitials = useMemo(() => {
    return (name = "User") =>
      name
        .split(" ")
        .map((w) => w[0])
        .join("")
        .toUpperCase()
        .substring(0, 2);
  }, []);

  const renderComment = (data) => {
    // Extract user name from the API response structure
    const firstName = data?.createdBy?.name?.firstName || "";
    const lastName = data?.createdBy?.name?.lastName || "";
    const userName = `${firstName} ${lastName}`.trim() || "User";
    const userInitials = getUserInitials(userName);

    // Format the created date
    const formatDate = (dateString) => {
      if (!dateString) return "Just now";
      try {
        const date = new Date(dateString);
        const now = new Date();
        const diffInMinutes = Math.floor((now - date) / (1000 * 60));

        if (diffInMinutes < 1) return "Just now";
        if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
        if (diffInMinutes < 1440)
          return `${Math.floor(diffInMinutes / 60)}h ago`;
        return date.toLocaleDateString();
      } catch {
        return "Just now";
      }
    };

    return (
      <div>
        <Flex
          align="center"
          justify="space-between"
          style={{ paddingBottom: 4 }}
        >
          <Flex gap={6} align="center">
            <div
              style={{
                width: 24,
                height: 24,
                borderRadius: "50%",
                backgroundColor: "#1890ff",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 10,
                color: "#fff",
              }}
            >
              {userInitials}
            </div>
            <span style={{ fontSize: 13, fontWeight: 500 }}>{userName}</span>
          </Flex>
        </Flex>

        <p style={{ fontSize: 13, padding: "4px 0" }}>{data?.content}</p>
        <Flex gap={12} align="center" style={{ fontSize: 12, color: "#888" }}>
          <span>{formatDate(data?.createdAt)}</span>
        </Flex>
      </div>
    );
  };

  const handleSubmit = async () => {
    if (!commentText.trim() || !entityId) return;

    setLoading(true);
    try {
      const commentData = {
        content: commentText.trim(),
      };

      await dispatch(addEntityCommentsAction(entityId, commentData));

      // Refresh comments and activity logs after successful submission
      dispatch(getEntityCommentsAction(entityId));
      dispatch(getEntityActivityLogsAction(entityId));

      setCommentText("");
    } catch (error) {
      console.error("Failed to add comment:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {showCard && !drawerOpen && (
        <Card
          title={
            <Flex
              align="center"
              justify="space-between"
              style={{
                borderBottom: themeDefault?.info?.newConfig?.cardborder,
                width: "100%",
                paddingBottom: 8,
              }}
            >
              Comments
            </Flex>
          }
          style={{
            marginBottom: 16,
            display: "flex",
            flexDirection: "column",
            padding: 16,
            paddingBottom: 4,
          }}
          bodyStyle={{ flex: 1, overflowY: "auto", padding: 16 }}
        >
          {commentsLoading ? (
            <div style={{ textAlign: "center", padding: 20 }}>
              <CustomLoaderIcon />
            </div>
          ) : commentsData.length > 0 ? (
            commentsData.slice(0, 1).map((comment) => (
              <div key={comment._id} style={{ marginBottom: 4 }}>
                {renderComment(comment)}
              </div>
            ))
          ) : (
            <Flex
              align="center"
              className={css`
                height: 100%;
              `}
            >
              <MoreEmptyState
                type="ErrorState"
                size="small"
                title="No comments yet"
              />
            </Flex>
          )}
          <Button
            type="link"
            onClick={() => setDrawerOpen(true)}
            style={{
              width: "100%",
              marginTop: "auto",
              color: isLightBg ? "#000000" : "#ffffff",
              border: themeDefault?.info?.newConfig?.cardborder,
              borderBottom: "none",
              borderRight: "none",
              borderLeft: "none",
              borderRadius: 0,
            }}
          >
            <MaximizeIcon size="small" />
            Comments
          </Button>
        </Card>
      )}

      <Drawer
        title="All Comments"
        placement="right"
        onClose={() => {
          setDrawerOpen(false);
          onClose && onClose();
        }}
        open={drawerOpen}
        width={720}
        styles={{ body: { padding: 16 } }}
        footer={
          <div
            style={{
              padding: "12px 24px",
              backgroundColor:
                themeDefault?.info?.newConfig?.cardBackground || "#fff",
            }}
          >
            <Flex gap={8} align="center">
              <Input
                placeholder="Add a comment..."
                value={commentText}
                onChange={(e) => setCommentText(e.target.value)}
                onPressEnter={handleSubmit}
                style={{ flex: 1 }}
                disabled={loading}
              />
              <Button type="primary" onClick={handleSubmit} loading={loading}>
                <MessageTextIcon size="small" />
              </Button>
            </Flex>
          </div>
        }
      >
        {commentsLoading ? (
          <div style={{ textAlign: "center", padding: 40 }}>
            <CustomLoaderIcon />
          </div>
        ) : commentsData.length > 0 ? (
          commentsData.map((comment) => (
            <Card key={comment._id} style={{ marginBottom: 12 }}>
              {renderComment(comment)}
            </Card>
          ))
        ) : (
          <Flex
            align="center"
            className={css`
              height: 100%;
            `}
          >
            <MoreEmptyState
              type="ErrorState"
              size="small"
              text="No comments yet"
            />
          </Flex>
        )}
      </Drawer>
    </>
  );
};

export default CommentsCard;
